import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { AllowedTenantsProjection } from 'app/shared/prothesis-api-projections/allowed-tenants-projection';
import { InfoUserCustomerProjection } from 'app/shared/prothesis-api-projections/info-user-customer.projection';
import { InfoUserLaboratoryProjection } from 'app/shared/prothesis-api-projections/info-user-laboratory.projection';
import { BehaviorSubject, EMPTY, Observable, ReplaySubject, combineLatest } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, retry, switchMap } from 'rxjs/operators';
import { Area } from '../../shared/enums/area.enum';
import { BearerCookieService } from './bearer-cookie.service';
import { ConfigurationService } from './configuration.service';
import { DatadogContextStateService } from './datadog-context-state.service';
import { LocalStorageService } from './local-storage.service';
import { NotifyService } from './notify/notify.service';
import { WorkingLaboratoryService } from './prothesis-api/working-laboratory.service';
import { WorkingCustomerService } from './prothesis-customer-api/working-customer.service';
import { SessionStorageService } from './session-storage.service';

/**
 * Prefix of the `token_validation_error` reason raised by angular-oauth2-oidc when the id_token
 * returned by a silent refresh carries another account's `sub`. The library reports it as a plain
 * string, whereas the `silent_refresh_error` that follows nests the whole event, so matching here
 * avoids any unwrapping.
 */
const strForeignIdentityReasonPrefix = 'After refreshing, we got an id_token for another user';

/**
 * https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  public static strKeyArea = 'area';
  public static strKeyCustomerClaim = 'customer-claim';
  public static strKeyLaboratoryClaim = 'laboratory-claim';

  public areaConfiguredFlag$: Observable<boolean>;
  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean>;
  public initErrorFlag$: Observable<boolean>;
  public isAccessTokenAboutToExpire$: Observable<boolean>;
  public isAuthenticated$: Observable<boolean>;
  public isDoneLoading$: Observable<boolean>;

  private _allowedCustomerUuids = new Array<string>();
  private _allowedLaboratoryUuids = new Array<string>();
  private _area: Area;
  private _areaConfiguredFlag$ = new BehaviorSubject<boolean>(false);
  private _bearerCookieService = inject(BearerCookieService);
  private _configurationService = inject(ConfigurationService);
  private _contextStateService = inject(DatadogContextStateService);
  private _http = inject(HttpClient);
  private _identityServerAddress: string;
  private _initErrorFlagSubject$ = new BehaviorSubject<boolean>(false);
  private _isAccessTokenAboutToExpireSubject$ = new BehaviorSubject<boolean>(true);
  private _isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  private _isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  private _isLoggingOutFlag = false;
  // One-way latch, like _isLoggingOutFlag: never lowered, because login() navigates away and the
  // reload starts a new AuthService at false. Only a login URL that fails to build (thrown by the
  // HTTPS check, or swallowed by the library) would keep it raised, and that is static configuration;
  // the tab is signed out by logOut(true) either way and a reload recovers.
  private _isReauthenticatingFlag = false;
  private _localStorageService = inject(LocalStorageService);
  private _notifyService = inject(NotifyService);
  private _oauthService = inject(OAuthService);
  private _router = inject(Router);
  private _sessionStorageService = inject(SessionStorageService);
  private _workingCustomerService = inject(WorkingCustomerService);
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public constructor() {
    const _workingLaboratoryService = this._workingLaboratoryService;
    const _workingCustomerService = this._workingCustomerService;

    _workingCustomerService.authService = this;
    _workingLaboratoryService.authService = this;

    // Useful for debugging:
    // https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
    this._oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.warn(event);
      } else {
        if (event.type !== 'session_unchanged') {
          console.log(event);
        }
      }
    });

    // This is tricky, as it might cause race conditions (where access_token is set in another tab before everything is said and done there.
    // Note: The storage event is only triggered when a window other than itself makes the changes.
    // https://www.w3schools.com/jsref/tryit.asp?filename=tryjsref_event_storage_storage
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key) {
        return;
      }

      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      const validAccessTokenFlag: boolean = this._oauthService.hasValidAccessToken();
      this._isAuthenticatedSubject$.next(validAccessTokenFlag);
      this._isAccessTokenAboutToExpireSubject$.next(this._isAccessTokenAboutToExpire());

      if (!validAccessTokenFlag) {
        this.login();
      }
    });

    this._oauthService.events.subscribe(() => {
      this._isAuthenticatedSubject$.next(this._oauthService.hasValidAccessToken());
      this._isAccessTokenAboutToExpireSubject$.next(this._isAccessTokenAboutToExpire());
    });

    this._oauthService.events
      .pipe(filter((e) => ['token_received'].includes(e.type)))
      // loadUserProfile already logs its own failure ("error loading user info"); the catch prevents the
      // rejected promise from additionally surfacing as an uncaught error (notably the startsWith-on-null
      // TypeError thrown by the lib when the userinfo response has no Content-Type header — upstream bug).
      .subscribe(() => this._oauthService.loadUserProfile().catch(() => undefined));

    // Mirror the JWT into an HttpOnly cookie on each login/refresh so browser navigations
    // (such as the Swagger UI) can authenticate without an Authorization header.
    // A single token acquisition emits several of these events (e.g. token_received + silently_refreshed
    // during a silent refresh), so distinctUntilChanged collapses the redundant, identical cookie writes,
    // and switchMap cancels any in-flight write superseded by a newer token. retry rides out a transient
    // failure (5xx, network) without waiting for the next OAuth event; catchError then swallows a final
    // failure so it never terminates the outer events subscription (which would stop all future mirroring).
    this._oauthService.events
      .pipe(
        filter((e) => ['token_received', 'token_refreshed', 'silently_refreshed'].includes(e.type)),
        map(() => this.accessToken),
        filter((token) => !!token),
        distinctUntilChanged(),
        switchMap((token) =>
          this._bearerCookieService.setBearerAsCookie(token).pipe(
            retry({ count: 3, delay: 2000 }),
            catchError(() => EMPTY),
          ),
        ),
      )
      .subscribe();

    // Tokens live in sessionStorage (per tab) while the identity provider session cookie is
    // browser-wide and only remembers the last account signed in. When a second account signs in,
    // this tab's silent refresh comes back with that other identity and the library rejects it.
    // Recover locally instead of logging out: see _reauthenticateLocally.
    this._oauthService.events.pipe(filter((e) => this._isSessionReplacedOrLostEvent(e))).subscribe(() => this._reauthenticateLocally());

    this._oauthService.events.pipe(filter((e) => e.type === 'session_error')).subscribe(() => this.logout());

    this._oauthService.setupAutomaticSilentRefresh();

    this.areaConfiguredFlag$ = this._areaConfiguredFlag$.asObservable();
    this.initErrorFlag$ = this._initErrorFlagSubject$.asObservable();
    this.isAccessTokenAboutToExpire$ = this._isAccessTokenAboutToExpireSubject$.asObservable();
    this.isAuthenticated$ = this._isAuthenticatedSubject$.asObservable();
    this.isDoneLoading$ = this._isDoneLoadingSubject$.asObservable();
    this.canActivateProtectedRoutes$ = combineLatest([this.isAuthenticated$, this.isDoneLoading$]).pipe(
      map((values) => values.every((b) => b)),
    );
  }

  public get accessToken(): string {
    return this._oauthService.getAccessToken();
  }

  public get accessTokenClaims(): Record<string, unknown> | null {
    const accessToken = this._oauthService.getAccessToken();

    if (!accessToken) {
      return null;
    }

    const jwtBase64EncodedBody = accessToken.split('.')[1];
    const jwtDecodedBody = window.atob(jwtBase64EncodedBody);
    const jwtBody = JSON.parse(jwtDecodedBody);

    return jwtBody;
  }

  public get allowedCustomerUuids(): string[] {
    return this._allowedCustomerUuids;
  }

  public get allowedLaboratoryUuids(): string[] {
    return this._allowedLaboratoryUuids;
  }

  public get area(): Area | null | undefined {
    return this._area;
  }

  public set area(value: Area) {
    this._area = value;

    if (this._area) {
      this._sessionStorageService.write(AuthService.strKeyArea, this._area);
    } else {
      this._sessionStorageService.removeItem(AuthService.strKeyArea);
    }
  }

  public get identityClaims(): object {
    return this._oauthService.getIdentityClaims();
  }

  public get identityServerAddress(): string {
    return this._identityServerAddress;
  }

  public get idToken(): string {
    return this._oauthService.getIdToken();
  }

  public get isAccessTokenAboutToExpire(): boolean {
    const result = this._isAccessTokenAboutToExpire();
    this._isAccessTokenAboutToExpireSubject$.next(result);
    return result;
  }

  public get isAdminOrSuperAdminFlag(): boolean {
    return this._isInRole('admin') || this._isInRole('superAdmin');
  }

  /*
   * Returns false if user not authenticated or access token is about to expire.
   * For instance if the access token has expired this method will return false.
   */
  public get isAuthenticated(): boolean {
    const validAccessTokenFlag = this._oauthService.hasValidAccessToken();
    this._isAuthenticatedSubject$.next(validAccessTokenFlag);
    return validAccessTokenFlag;
  }

  public get isLoggingOutFlag(): boolean {
    return this._isLoggingOutFlag;
  }

  /**
   * True once the tab has committed to leaving for the identity provider, through a logout or a
   * local re-authentication. The `beforeunload` guards read it to stay silent: the tokens
   * are already being dropped, so letting the user stay would only strand the tab.
   */
  public get isRedirectingToIdentityProviderFlag(): boolean {
    return this._isLoggingOutFlag || this._isReauthenticatingFlag;
  }

  public get isSuperAdminFlag(): boolean {
    return this._isInRole('superAdmin');
  }

  public get logoutUrl(): string | undefined {
    return this._oauthService.logoutUrl;
  }

  public get name(): string | null {
    const claims = this._oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }

    return claims['preferred_username'];
  }

  public get roles(): string | string[] | null {
    if (!this.accessTokenClaims) {
      return null;
    }

    return this.accessTokenClaims['role'] as string | string[];
  }

  public get userUuid(): string | null {
    if (!this.accessTokenClaims) {
      return null;
    }

    return this.accessTokenClaims['user_uuid'] as string;
  }

  public configureCustomerArea(customerUuid: string | null = null): Promise<void> {
    this.area = Area.Customer;

    if (!customerUuid) {
      customerUuid = this._getDefaultCustomerUuidToAuthenticate();
    }
    this._workingCustomerService.tenantUuid = customerUuid;

    this._sessionStorageService.write(AuthService.strKeyArea, JSON.stringify(Area.Customer));
    this._sessionStorageService.write(AuthService.strKeyCustomerClaim, customerUuid);
    this._sessionStorageService.removeItem(AuthService.strKeyLaboratoryClaim);

    this._localStorageService.write(AuthService.strKeyArea, JSON.stringify(Area.Customer));
    this._localStorageService.write(AuthService.strKeyCustomerClaim, customerUuid);
    this._localStorageService.removeItem(AuthService.strKeyLaboratoryClaim);

    return this.getInfoToRefreshCustomer(customerUuid);
  }

  public configureLaboratoryArea(laboratoryUuid: string | null = null): Promise<void> {
    this.area = Area.Laboratory;

    if (!laboratoryUuid) {
      laboratoryUuid = this._getDefaultLaboratoryUuidToAuthenticate();
    }
    this._workingLaboratoryService.tenantUuid = laboratoryUuid;

    this._sessionStorageService.write(AuthService.strKeyLaboratoryClaim, laboratoryUuid);
    this._sessionStorageService.removeItem(AuthService.strKeyCustomerClaim);
    this._sessionStorageService.write(AuthService.strKeyArea, JSON.stringify(Area.Laboratory));

    this._localStorageService.write(AuthService.strKeyArea, JSON.stringify(Area.Laboratory));
    this._localStorageService.write(AuthService.strKeyLaboratoryClaim, laboratoryUuid);
    this._localStorageService.removeItem(AuthService.strKeyCustomerClaim);

    return this.getInfoToRefreshLaboratory(laboratoryUuid);
  }

  public getAllowedTenants(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Add reject for error handling
      this._notifyService.doNotDismissOnNextApiCall();
      const actionUrl = `${this._configurationService.ProthesisApiAddress}/authenticated/User/GetAllowedTenants`;

      this._http.get(actionUrl).subscribe({
        // Use an object for subscribe to handle errors
        next: (allowedTenantsProjection: AllowedTenantsProjection) => {
          // Normalise the absence HERE, once, so the two getters can honestly promise `string[]`.
          // This is the incoming boundary; the rule against coalescing covers view-models built
          // *before being sent*, not responses being read.
          //
          // Nine of the eleven consumer sites read a LENGTH (`?.length > 0|1`, `!== 1`) or call
          // `.indexOf` with no guard at all, so `[]` and `undefined` are indistinguishable to them
          // — the latter two are the ones this actually protects. Two sites evaluate the LIST
          // itself, where the two do diverge: `app.component.ts:129,131` push `AreaMenu.Laboratory`
          // / `AreaMenu.CustomerMember` on a truthy `[]` where `undefined` would fall through to
          // `AreaMenu.NoArea`. That divergence is unreachable in practice, and by construction, not
          // by luck: `AuthenticatedUserRepository.GetAllowedTenantsAsync` assigns both properties on
          // every path — the `.Select(… .ToList())` projection, or the `?? new AllowedTenantsProjection`
          // fallback with two empty lists. The API can return neither null nor an absent key, so the
          // `?? []` below never fires today. It exists because the response TYPE cannot promise that.
          this._allowedCustomerUuids = allowedTenantsProjection.allowedCustomerUuids ?? [];
          this._allowedLaboratoryUuids = allowedTenantsProjection.allowedLaboratoryUuids ?? [];
          resolve(); // Call resolve when the HTTP request is successful
        },
        error: (error) => {
          // 401 errors are handled by ApiInterceptor (redirects to login) — do not log as error
          if (!(error instanceof HttpErrorResponse && error.status === 401)) {
            console.error('Error fetching allowed tenants /authenticated/User/GetAllowedTenants', error);
          }
          reject(error);
        },
      });
    });
  }

  public getInfoToRefreshCustomer(customerUuid: string | null | undefined = null): Promise<void> {
    if (!customerUuid) {
      customerUuid = this._getDefaultCustomerUuidToExplore();
    }
    return new Promise<void>((successCallback) => {
      if (customerUuid) {
        this._notifyService.doNotDismissOnNextApiCall();
        this._workingCustomerService.getInfo(customerUuid).subscribe((infoUserCustomerProjection: InfoUserCustomerProjection) => {
          this._workingCustomerService.customerCode = infoUserCustomerProjection.customer.code;
          this._workingCustomerService.customerGroupFlag = infoUserCustomerProjection.customer.customerGroupFlag;
          this._workingCustomerService.customerGroupName = infoUserCustomerProjection.customer.customerGroupName;
          this._workingCustomerService.customerMemberFlag = infoUserCustomerProjection.customer.customerMemberFlag;
          this._workingCustomerService.customerName = infoUserCustomerProjection.customer.name;
          this._workingCustomerService.tenantUuid = infoUserCustomerProjection.customer.uuid;
          this._workingLaboratoryService.laboratoryCode = infoUserCustomerProjection.customer.laboratoryCode;
          this._workingLaboratoryService.laboratoryDemoModeFlag = infoUserCustomerProjection.customer.demoModeFlag;
          this._workingLaboratoryService.laboratoryName = infoUserCustomerProjection.customer.laboratoryName;
          this._workingLaboratoryService.laboratoryRateAutoPublishFlag = infoUserCustomerProjection.customer.rateAutoPublishFlag;
          this._workingLaboratoryService.laboratoryTzDatabaseName = infoUserCustomerProjection.customer.tzDatabaseName;
          // Emit before setting tenantUuid: in the customer area the laboratory tenantUuid is still
          // unset (null), so subscribers stop pulling laboratory notifications and reset their counters.
          this._workingLaboratoryService.emitExploredUuid();
          this._workingLaboratoryService.tenantUuid = infoUserCustomerProjection.customer.laboratoryUuid;

          this._contextStateService.setUserUuid(this.userUuid);
          this._contextStateService.setLaboratoryCode(null);

          this._areaConfiguredFlag$.next(true);
          successCallback();
        });
      } else {
        successCallback();
      }
    });
  }

  public getInfoToRefreshLaboratory(laboratoryUuid: string | null | undefined = null): Promise<void> {
    if (!laboratoryUuid) {
      laboratoryUuid = this._getDefaultLaboratoryUuidToExplore();
    }
    return new Promise<void>((successCallback) => {
      if (laboratoryUuid) {
        this._notifyService.doNotDismissOnNextApiCall();
        this._workingLaboratoryService.getInfo(laboratoryUuid).subscribe((infoUserLaboratoryProjection: InfoUserLaboratoryProjection) => {
          this._workingLaboratoryService.laboratoryRole = infoUserLaboratoryProjection.laboratoryRole;
          // tenantUuid must be first because emitExploredUuid() emits on _exploredUuidSubject
          // using _tenantUuid, which must already hold the correct value.
          this._workingLaboratoryService.tenantUuid = infoUserLaboratoryProjection.laboratory.uuid;
          this._workingLaboratoryService.laboratoryCode = infoUserLaboratoryProjection.laboratory.code;
          this._workingLaboratoryService.laboratoryDemoModeFlag = infoUserLaboratoryProjection.laboratory.demoModeFlag;
          this._workingLaboratoryService.laboratoryName = infoUserLaboratoryProjection.laboratory.name;
          this._workingLaboratoryService.laboratoryLegalIdentifierSchemeCode =
            infoUserLaboratoryProjection.laboratory.legalIdentifierSchemeCode;
          this._workingLaboratoryService.laboratoryRateAutoPublishFlag = infoUserLaboratoryProjection.laboratory.rateAutoPublishFlag;
          this._workingLaboratoryService.laboratoryTzDatabaseName = infoUserLaboratoryProjection.laboratory.tzDatabaseName;
          // emitExploredUuid() emits on _exploredUuidSubject, so all properties above must already be set.
          this._workingLaboratoryService.emitExploredUuid();

          this._contextStateService.setUserUuid(this.userUuid);
          this._contextStateService.setCustomerCode(null);

          this._areaConfiguredFlag$.next(true);
          successCallback();
        });
      } else {
        successCallback();
      }
    });
  }

  public initialize(identityServerAddress: string): void {
    this._identityServerAddress = identityServerAddress;
    this._oauthService.configure({
      // https://github.com/manfredsteyer/angular-oauth2-oidc/issues/457#issuecomment-431807040
      clearHashAfterLogin: false,
      clientId: 'Prothesis',
      issuer: this._identityServerAddress,
      nonceStateSeparator: 'semicolon', // Real semicolon gets mangled by IdentityServer's URI encoding
      redirectUri: window.location.origin,
      requireHttps: false,
      responseType: 'code',

      // https://manfredsteyer.github.io/angular-oauth2-oidc/docs/additional-documentation/session-checks.html
      sessionChecksEnabled: true,
      showDebugInformation: false, // Also requires enabling "Verbose" level in devtools
      scope: 'api openid permissions profile',
      silentRefreshRedirectUri: window.location.origin + '/assets/angular-oauth2-oidc/silent-refresh.html',
      useSilentRefresh: true, // Needed for Code Flow to suggest using iframe-based refreshes
    });
  }

  public login(targetUrl?: string): void {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.

    // Use window.location instead of this._router.url as fallback: at bootstrap,
    // the Angular router has not yet committed the pending navigation blocked by
    // AuthGuardWithForcedLogin, so _router.url is still '/' even when the user
    // requested a deep URL like '/patient'. window.location is always accurate.
    this._oauthService.initLoginFlow(targetUrl || window.location.pathname + window.location.search);
  }

  public logout(): void {
    this._isLoggingOutFlag = true;
    this._contextStateService.clearContext();
    // Wait for the cookie deletion to complete before the OIDC redirect navigates away,
    // otherwise the redirect races the Set-Cookie deletion and the Bearer cookie can survive
    // with a still-valid JWT (the APIs validate it locally, without revocation). finalize
    // runs logOut() whether the deletion succeeds or fails, so logout is never blocked.
    this._bearerCookieService
      .clearBearerCookie()
      .pipe(finalize(() => this._oauthService.logOut()))
      .subscribe();
  }

  public refresh(): Promise<OAuthEvent> {
    return this._oauthService.silentRefresh();
  }

  public runInitialLoginSequence(): Promise<void> {
    if (location.hash) {
      console.log('Encountered hash fragment, plotting as table...');
      console.table(
        location.hash
          .substr(1)
          .split('&')
          .map((kvp) => kvp.split('=')),
      );
    }

    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    return (
      this._oauthService
        .loadDiscoveryDocument()

        // 1. HASH LOGIN:
        // Try to log in via hash fragment after redirect back
        // from IdServer from initImplicitFlow:
        .then(() => this._oauthService.tryLogin())

        .then(() => {
          if (this._oauthService.hasValidAccessToken()) {
            return Promise.resolve();
          }

          // 2. SILENT LOGIN:
          // Try to log in via a refresh because the IdServer
          // might have a cookie to remember the user, so we can
          // prevent doing a redirect:
          return this._oauthService
            .silentRefresh()
            .then(() => Promise.resolve())
            .catch((result) => {
              // Handle silent refresh errors that are "normal"
              const normalSilentRefreshErrors = [
                'silent_refresh_error',
                'silent_refresh_timeout',
                'invalid_nonce_in_state',
                'token_validation_error',
              ];

              if (result?.reason?.type && normalSilentRefreshErrors.includes(result.reason.type)) {
                console.warn(
                  'Silent refresh or token error, we will wait for the user to manually log in or for the next silent retry success.',
                );
                return Promise.resolve();
              }

              // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
              // Only the ones where it's reasonably sure that sending the
              // user to the IdServer will help.
              const errorResponsesRequiringUserInteraction = [
                'interaction_required',
                'login_required',
                'account_selection_required',
                'consent_required',
              ];

              if (result && result.reason && errorResponsesRequiringUserInteraction.indexOf(result.reason.error) >= 0) {
                // 3. ASK FOR LOGIN:
                // At this point we know for sure that we have to ask the
                // user to log in, so we redirect them to the IdServer to
                // enter credentials.
                //
                // Enable this to ALWAYS force a user to login.
                // this.login();
                //
                // Instead, we'll now do this:
                console.warn('User interaction is needed to log in, we will wait for the user to manually log in.');
                return Promise.resolve();
              }

              // We can't handle the truth, just pass on the problem to the
              // next handler.
              return Promise.reject(result);
            });
        })

        .then(() => this.getAllowedTenants())

        .then(() => {
          this._workingCustomerService.isAdminOrSuperAdminFlag = this.isAdminOrSuperAdminFlag;
          if (this.isAdminOrSuperAdminFlag) {
            this.area = Area.Admin;
            this._endInitialLoginSequence();
            return Promise.resolve();
          } else {
            const connexionData: { area: Area; tenantUuid: string | null } | null = this._getDataFromSessionStorageThenLocalStorage();

            if (connexionData.area === Area.Laboratory) {
              return this.configureLaboratoryArea(connexionData.tenantUuid).then(() => {
                this._endInitialLoginSequence();
              });
            } else if (connexionData.area === Area.Customer) {
              return this.configureCustomerArea(connexionData.tenantUuid).then(() => {
                this._endInitialLoginSequence();
              });
            } else {
              this.area = Area.NoArea;
              this._endInitialLoginSequence();
              return Promise.resolve();
            }
          }
        })
        .catch((error) => {
          // Do not set isDoneLoading to prevent the app from rendering with incomplete state.
          // On 401 or OAuth errors (e.g. silent_refresh_error when session expired), redirect to login.
          // On other errors (e.g. 500), display the error page — ApiInterceptor already shows the error message.
          if ((error instanceof HttpErrorResponse && error.status === 401) || error instanceof OAuthErrorEvent) {
            this.login();
          } else {
            console.error('Error in runInitialLoginSequence:', error);
            this._initErrorFlagSubject$.next(true);
          }
        })
    );
  }

  public setAreaConfiguredFlag(): void {
    this._areaConfiguredFlag$.next(true);
  }

  private _endInitialLoginSequence(): void {
    // Check for the strings 'undefined' and 'null' just to be sure. Our current
    // login(...) should never have this, but in case someone ever calls
    // initImplicitFlow(undefined | null) this could happen.
    if (this._oauthService.state && this._oauthService.state !== 'undefined' && this._oauthService.state !== 'null') {
      let stateUrl = this._oauthService.state;
      if (stateUrl.startsWith('/') === false) {
        stateUrl = decodeURIComponent(stateUrl);
      }
      console.log(`There was state of ${this._oauthService.state}, so we are sending you to: ${stateUrl}`);
      this._router.navigateByUrl(stateUrl);
    }

    this._isDoneLoadingSubject$.next(true);
  }

  private _getDataFromLocalStorage(): { area: Area; tenantUuid: string | null } {
    const areaFromLocalStorage = this._localStorageService.tryGetArea(AuthService.strKeyArea);

    if (!(this.allowedLaboratoryUuids?.length > 0 || this.allowedCustomerUuids?.length > 0)) {
      return { area: Area.NoArea, tenantUuid: null };
    }

    if (areaFromLocalStorage === Area.Laboratory) {
      if (this.allowedLaboratoryUuids?.length > 0) {
        const laboratoryUuidFromSessionStorage = this._localStorageService.read<string>(AuthService.strKeyLaboratoryClaim);
        if (laboratoryUuidFromSessionStorage !== null && this.allowedLaboratoryUuids.indexOf(laboratoryUuidFromSessionStorage) !== -1) {
          return { area: Area.Laboratory, tenantUuid: laboratoryUuidFromSessionStorage };
        } else {
          return { area: Area.Laboratory, tenantUuid: null };
        }
      } else {
        return { area: Area.Customer, tenantUuid: null };
      }
    } else if (areaFromLocalStorage === Area.Customer) {
      if (this.allowedCustomerUuids?.length > 0) {
        const customerUuidFromSessionStorage = this._localStorageService.read<string>(AuthService.strKeyCustomerClaim);
        if (customerUuidFromSessionStorage !== null && this.allowedCustomerUuids.indexOf(customerUuidFromSessionStorage) !== -1) {
          return { area: Area.Customer, tenantUuid: customerUuidFromSessionStorage };
        } else {
          return { area: Area.Customer, tenantUuid: null };
        }
      } else {
        return { area: Area.Laboratory, tenantUuid: null };
      }
    } else {
      if (this.allowedLaboratoryUuids?.length > 0) {
        return { area: Area.Laboratory, tenantUuid: null };
      } else {
        return { area: Area.Customer, tenantUuid: null };
      }
    }
  }

  private _getDataFromSessionStorageThenLocalStorage(): { area: Area; tenantUuid: string | null } {
    const areaFromSessionStorage = this._sessionStorageService.tryGetArea(AuthService.strKeyArea);

    if (!(this.allowedLaboratoryUuids?.length > 0 || this.allowedCustomerUuids?.length > 0)) {
      return { area: Area.NoArea, tenantUuid: null };
    }

    if (areaFromSessionStorage === Area.Laboratory) {
      if (this.allowedLaboratoryUuids?.length > 0) {
        const laboratoryUuidFromSessionStorage = this._sessionStorageService.read<string>(AuthService.strKeyLaboratoryClaim);
        if (laboratoryUuidFromSessionStorage !== null && this.allowedLaboratoryUuids.indexOf(laboratoryUuidFromSessionStorage) !== -1) {
          return { area: Area.Laboratory, tenantUuid: laboratoryUuidFromSessionStorage };
        }
      }
    } else if (areaFromSessionStorage === Area.Customer) {
      if (this.allowedCustomerUuids?.length > 0) {
        const customerUuidFromSessionStorage = this._sessionStorageService.read<string>(AuthService.strKeyCustomerClaim);
        if (customerUuidFromSessionStorage !== null && this.allowedCustomerUuids.indexOf(customerUuidFromSessionStorage) !== -1) {
          return { area: Area.Customer, tenantUuid: customerUuidFromSessionStorage };
        }
      }
    }

    return this._getDataFromLocalStorage();
  }

  private _getDefaultCustomerUuidToAuthenticate(): string | null {
    const workingCustomerUuid = this._sessionStorageService.read<string>('working_customer_uuid');
    if (workingCustomerUuid) {
      if (this.allowedCustomerUuids.includes(workingCustomerUuid)) {
        return workingCustomerUuid;
      } else {
        return this._getFirstCustomerUuid();
      }
    } else {
      return this._getFirstCustomerUuid();
    }
  }

  private _getDefaultCustomerUuidToExplore(): string | undefined {
    const workingCustomerUuid = this._sessionStorageService.read<string>('working_customer_uuid');
    if (workingCustomerUuid) {
      return workingCustomerUuid;
    } else {
      return undefined;
    }
  }

  private _getDefaultLaboratoryUuidToAuthenticate(): string | null {
    const workingLaboratoryUuid = this._sessionStorageService.read<string>('working_laboratory_uuid');
    if (workingLaboratoryUuid) {
      if (this.allowedLaboratoryUuids.includes(workingLaboratoryUuid)) {
        return workingLaboratoryUuid;
      } else {
        return this._getFirstLaboratoryUuid();
      }
    } else {
      return this._getFirstLaboratoryUuid();
    }
  }

  private _getDefaultLaboratoryUuidToExplore(): string | undefined {
    const workingLaboratoryUuid = this._sessionStorageService.read<string>('working_laboratory_uuid');
    if (workingLaboratoryUuid) {
      return workingLaboratoryUuid;
    } else {
      return undefined;
    }
  }

  private _getFirstCustomerUuid(): string | null {
    if (this.allowedCustomerUuids instanceof Array && this.allowedCustomerUuids.length > 0) {
      return this.allowedCustomerUuids[0];
    } else {
      return null;
    }
  }

  private _getFirstLaboratoryUuid(): string | null {
    if (this.allowedLaboratoryUuids instanceof Array && this.allowedLaboratoryUuids.length > 0) {
      return this.allowedLaboratoryUuids[0];
    } else {
      return null;
    }
  }

  private _isAccessTokenAboutToExpire(): boolean {
    const accessTokenExpiration = this._oauthService.getAccessTokenExpiration();
    const now = Date.now();
    const delta = accessTokenExpiration - now;

    if (delta < 30000) {
      return true;
    }

    return false;
  }

  private _isInRole(role: string): boolean {
    let isInRoleFlag = false;
    if (Array.isArray(this.roles)) {
      isInRoleFlag = this.roles.some((r) => r === role);
    } else {
      isInRoleFlag = this.roles === role;
    }

    return isInRoleFlag;
  }

  private _isSessionReplacedOrLostEvent(event: OAuthEvent): boolean {
    // Broader than a foreign identity: the library also emits session_terminated when the provider
    // session ended (logout in another tab, expiry) or when the refresh after a session change timed
    // out. It has already wiped the tokens then, so reconnecting stays the right answer.
    if (event.type === 'session_terminated') {
      return true;
    }

    // A foreign identity alone, when the automatic refresh brings it back before any session check
    // noticed the change: no session_terminated follows in that case.
    if (event.type !== 'token_validation_error') {
      return false;
    }

    // Typed `object` by the library but a plain string at runtime for this failure, hence `unknown`.
    const reason: unknown = (event as OAuthErrorEvent).reason;

    return typeof reason === 'string' && reason.startsWith(strForeignIdentityReasonPrefix);
  }

  private _reauthenticateLocally(): void {
    // A user-triggered logout is already redirecting to end_session; the session probe can report
    // session_terminated inside that window, and logging back in there would override the departure
    // the user asked for. The reauthenticating flag is raised before the asynchronous cookie
    // deletion below, so a replayed event landing mid-flight is turned away just the same.
    if (this._isReauthenticatingFlag || this._isLoggingOutFlag) {
      return;
    }

    // Also silences the beforeunload guards (see isRedirectingToIdentityProviderFlag): a prompt on a
    // dirty form would let the user stay on a tab whose tokens logOut(true) is about to wipe.
    this._isReauthenticatingFlag = true;
    // The Datadog telemetry context belongs to the account being left and must not tag the next
    // session's RUM events. The tenant needs no reset: login() reloads the page, and the claims kept
    // in sessionStorage are checked against the new account's allowed tenants on the way back in.
    this._contextStateService.clearContext();

    // Same ordering as logout(): wait for the Bearer cookie deletion before navigating away, so the
    // leaving account's still-valid JWT cannot survive into the session that signs back in (the APIs
    // validate it locally, without revocation). finalize runs whether the deletion succeeds or
    // fails, so a failed deletion never strands the tab in the zombie state this fix removes.
    //
    // Local reset only: a full logOut() would redirect to end_session and destroy the browser-wide
    // provider session the other account just opened, ejecting that tab in turn (and so on, in a
    // loop). login() then reconnects this tab on whichever account is currently signed in.
    // UX variant left to a later decision: warn through a blocking dialog before redirecting.
    this._bearerCookieService
      .clearBearerCookie()
      .pipe(
        finalize(() => {
          // Re-read the flag: the user can click "log out" while the deletion is in flight, and
          // resetting here would override the end_session redirect their logout already started.
          if (this._isLoggingOutFlag) {
            return;
          }

          this._oauthService.logOut(true);
          this.login();
        }),
      )
      .subscribe();
  }
}
